import Vue from 'vue'
import i18n from '@ms/lang/i18n.js'
import { apiMoney } from '@ms/utils-es/math'
import { formatTaxParam } from '@ms/components/tax/api'
import { isSuspended, isUnavailable } from '@ms/components/betslip/core/update.js'
import { saveLocalSelections } from '@ms/components/betslip/core/save.js'
import { orderTypeMap } from '@ms/components/betslip/core/parser.js'
import { getPlaceSelections, getPlaceBets } from '@ms/components/betslip/core/getPlaceBody.js'
import { getUofId, isBetBuilderSelection } from './func'
import { useBetslipStore } from './index'
import { useSystemBetStore } from './useSystemBet'
import { useBankerStore } from './useBanker.js'

const MAGIC_PLAN = {
  FLEXIBLE: 1,
  SUPPLE: 2
}

const handleAllMultiplesMode = (multipleLength, systemStakeList) => {
  if (systemStakeList.length === 1) {
    if (systemStakeList[0].count === 1) return 'single'
    if (systemStakeList[0].count === multipleLength) return 'multiple'
  }
  return 'system'
}

const handleMultiplePlusMode = (magicPlan) => {
  switch (magicPlan) {
    case MAGIC_PLAN.FLEXIBLE:
      return 'flexible'
    case MAGIC_PLAN.SUPPLE:
      return 'supple'
    default:
      return ''
  }
}

const handleStandardMode = (standardMultipleStake, singleStakeList) => {
  const { stake, count } = standardMultipleStake
  if (!stake || count === 1) return 'single'
  return singleStakeList.length ? 'system' : 'multiple'
}

/**
 * Determines the order type based on various conditions.
 *
 * @param {number} multipleLength - The length of the multiple.
 * @param {boolean} isMultiplePlusMode - Indicates if the multiple plus mode is active.
 * @returns {string} - The order type, which can be `single`, `multiple`, `system`, `flexible`, or `supple`.
 */
export const getOrderType = (multipleLength, isMultiplePlusMode) => {
  const { isAllMultiplesMode, singleStakeList, standardMultipleStake, magicPlan } = useBetslipStore()
  if (isAllMultiplesMode) {
    const { systemStakeList } = useSystemBetStore()
    return handleAllMultiplesMode(multipleLength, systemStakeList)
  } else {
    // multiple+
    if (isMultiplePlusMode) {
      return handleMultiplePlusMode(magicPlan)
    }
    return handleStandardMode(standardMultipleStake, singleStakeList)
  }
}

/**
 * Updates the selections with the provided data list.
 *
 * @param {Object} data - The data object containing updated information for selections.
 * @property {Object} data[id] - The updated information for a specific selection.
 * @property {Object} data[id].outcome - The outcome information of the selection.
 * @property {boolean} data[id].outcome.isActive - Indicates if the outcome is active.
 * @property {Object} data[id].rest - The rest of the information for the selection.
 *
 * @returns {void}
 */
export const updateSelectionsWithResponse = (selectionList, data) => {
  if (Array.isArray(selectionList) === false) return
  if (data === null || typeof data !== 'object') return

  selectionList.forEach((x) => {
    if (isBetBuilderSelection(x)) {
      x.betBuilderSelections.forEach((subSelection) => {
        const { eventId, marketInfo, outcomeInfo } = subSelection
        const id = `${eventId}::${getUofId(subSelection)}`

        const cur = data[id]
        if (cur) {
          const { outcome, ...rest } = cur

          const { isActive } = outcome
          const eventStatus = subSelection.status
          const marketStatus = rest.status
          const suspendedStatus = isSuspended({ eventStatus, marketStatus, isActive })
          const unavailableStatus = isUnavailable({ eventStatus, marketStatus, isActive })
          const status = { isSuspended: suspendedStatus, isUnavailable: unavailableStatus }
          Object.assign(marketInfo, { ...rest })
          Object.assign(outcomeInfo, { ...outcome, ...status })
          if (outcomeInfo.isSuspended !== suspendedStatus) {
            Vue.set(outcomeInfo, 'isSuspended', suspendedStatus)
          }
          if (outcomeInfo.isUnavailable !== unavailableStatus) {
            Vue.set(outcomeInfo, 'isUnavailable', unavailableStatus)
          }
        }
      })
    } else {
      const { eventId, marketInfo, outcomeInfo } = x
      const id = `${eventId}::${getUofId(x)}`

      const cur = data[id]
      if (cur) {
        const { outcome, ...rest } = cur

        delete rest.description

        const { isActive } = outcome
        const eventStatus = x.status
        const marketStatus = rest.status
        const suspendedStatus = isSuspended({ eventStatus, marketStatus, isActive })
        const unavailableStatus = isUnavailable({ eventStatus, marketStatus, isActive })
        const status = { isSuspended: suspendedStatus, isUnavailable: unavailableStatus }
        Object.assign(marketInfo, { ...rest })
        Object.assign(outcomeInfo, { ...outcome, ...status })
        if (outcomeInfo.isSuspended !== suspendedStatus) {
          Vue.set(outcomeInfo, 'isSuspended', suspendedStatus)
        }
        if (outcomeInfo.isUnavailable !== unavailableStatus) {
          Vue.set(outcomeInfo, 'isUnavailable', unavailableStatus)
        }
      }
    }
  })

  // 更新本地信息
  saveLocalSelections(selectionList)
}

export const getPlaceBetBody = ({ orderType, formSelections, voucher, actualPayAmount, taxInfo }) => {
  const { magicNumber, isAllMultiplesMode, singleStakeMap, standardMultipleStake } = useBetslipStore()
  const { winningTax, extraBonus } = taxInfo

  const body = {
    bizType: 1, // 这里固定为 1 - realsport
    orderType: orderTypeMap[orderType],
    ticket: {
      selections: getPlaceSelections(formSelections, orderType, useBankerStore().bankerMap, singleStakeMap, isAllMultiplesMode),
      bets: getPlaceBets(orderType, {
        isAllMultiplesMode,
        magicNumber,
        singleStakeMap,
        standardMultipleStake,
        systemStakeList: useSystemBetStore().systemStakeList,
        selectionList: formSelections
      })
    },
    currency: i18n.t('CURRENCY')
  }
  // 使用红包
  if (voucher) {
    body.favor = {
      favorInfo: [
        {
          giftId: voucher.id
        }
      ]
    }
    // 实际支付金额，扣掉红包之后的金额，没用红包可以不填写, *10000之后的数值
    body.actualPayAmount = apiMoney(actualPayAmount)
  }

  if (winningTax) {
    Object.assign(body, {
      winningTax: formatTaxParam(winningTax),
      extraBonus: formatTaxParam(extraBonus)
    })
  }

  return body
}
